import React, { useState, useEffect } from 'react';
import { addDocument, getCollection } from '../services/firestoreService';
import { validateRut } from '../utils/validators';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  MenuItem,
  InputAdornment
} from '@mui/material';
import { REGIONES_COMUNAS } from '../utils/regionData';

const initialFormState = {
  ownerName: '',
  ownerRut: '',
  ownerCelular: '',
  ownerMail: '',
  ownerRegion: '',
  ownerComuna: '',
  ownerConvenio: '',
  patientName: '',
  species: '',
  breed: '',
  genero: '',
  birthDate: '',
  attentionNeed: '',
  priority: '',
  additionalInfo: '',
};

const AddContactRequestForm = ({ onAdd }) => {
  const { userData } = useAuth();
  const { activeBranchId, filterByBranch } = useBranch();
  const isDerivador = userData?.role === 'derivador';
  const derivadorConvenioName = isDerivador ? (userData?.convenioName || '') : '';

  const [formData, setFormData] = useState(() => ({
    ...initialFormState,
    ownerConvenio: derivadorConvenioName,
  }));
  const [needs, setNeeds] = useState([]);
  const [convenios, setConvenios] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [rutError, setRutError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadAuxData = async () => {
      try {
        const [needsData, conveniosData, breedsData] = await Promise.all([
          getCollection('needs'),
          getCollection('convenios'),
          getCollection('breeds')
        ]);
        setNeeds(filterByBranch(needsData.filter(n => n.isActive !== false), { includeUnassigned: true }));
        setConvenios(filterByBranch(conveniosData.filter(c => c.isActive !== false), { includeUnassigned: true }));
        setBreeds(breedsData || []);
      } catch (err) {
        console.error('Error loading needs/convenios/breeds:', err);
      }
    };
    loadAuxData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const updated = { ...prev, [name]: value };

      if (name === 'species') {
        updated.breed = '';
      }

      if (name === 'ownerRegion') {
        updated.ownerComuna = '';
      }
      
      // Si el usuario es Derivador, no permitir cambios manuales en Convenio
      if (name === 'ownerConvenio' && isDerivador) {
        return prev;
      }

      return updated;
    });

    if (name === 'ownerRut') {
      if (value && !validateRut(value)) {
        setRutError('El RUT ingresado no es válido.');
      } else {
        setRutError('');
      }
    }

    if (name === 'ownerCelular') {
      const digits = value.replace(/\D/g, '');
      if (!/^9\d{8}$/.test(digits)) {
        setPhoneError('Ingresa un celular válido de 9 dígitos, por ejemplo 9XXXXXXXX');
      } else {
        setPhoneError('');
      }
    }
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDifference = today.getMonth() - birth.getMonth();
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age.toString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (rutError) {
      setError('Por favor, corrija los errores en el RUT antes de continuar.');
      return;
    }

    if (phoneError) {
      setError('Por favor, corrija el teléfono antes de continuar.');
      return;
    }

    if (isDerivador && !formData.ownerConvenio) {
      setError('Tu usuario no tiene un convenio asociado. Contacta al administrador.');
      return;
    }

    const requiredFields = [
      'ownerName',
      'ownerRut',
      'ownerCelular',
      'ownerMail',
      'ownerRegion',
      'ownerComuna',
      'ownerConvenio',
      'patientName',
      'species',
      'breed',
      'genero',
      'birthDate',
      'attentionNeed',
      'priority',
      'additionalInfo',
    ];

    const missingFields = requiredFields.filter((field) => !String(formData[field] || '').trim());
    if (missingFields.length > 0) {
      setError('Por favor completa todos los campos obligatorios del formulario.');
      return;
    }

    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // 1. Crear propietario
      const phoneDigits = formData.ownerCelular.replace(/\D/g, '');
      const celularFull = phoneDigits ? `+56${phoneDigits}` : '';

      const ownerData = {
        name: formData.ownerName.trim(),
        rut: formData.ownerRut.trim(),
        celular: celularFull,
        mail: formData.ownerMail.trim(),
        region: formData.ownerRegion.trim(),
        comuna: formData.ownerComuna.trim(),
        convenio: formData.ownerConvenio || '',
      };
      const ownerId = await addDocument('owners', ownerData);

      // 2. Crear paciente
      const edad = calculateAge(formData.birthDate);
      const patientData = {
        name: formData.patientName.trim(),
        species: formData.species,
        breed: formData.breed.trim(),
        genero: formData.genero || '',
        birthDate: formData.birthDate || '',
        edad,
        ownerId,
      };
      const patientId = await addDocument('patients', patientData);

      // 3. Crear solicitud de contacto
      const selectedNeed = needs.find((n) => n.name === formData.attentionNeed);
      const requestData = {
        source: 'Manual',
        ownerName: ownerData.name,
        patientName: patientData.name,
        contactInfo: celularFull || ownerData.mail || '',
        attentionNeed: formData.attentionNeed || '',
        attentionNeedId: selectedNeed?.id || '',
        attentionNeedGoal: selectedNeed?.goalName || '',
        patientType: formData.ownerConvenio || 'Sin Convenio',
        assignedTo: '',
        ownerId,
        patientId,
        branchId: activeBranchId || '',
        createdAt: new Date(),
        status: 'Nuevo',
        priority: formData.priority || 'Media',
        additionalInfo: formData.additionalInfo || '',
      };

      const docId = await addDocument('contact-requests', requestData);
      setSuccess(`Tu solicitud fue enviada con éxito. ID de referencia: ${docId}`);
      if (onAdd) {
        onAdd();
      }

      setFormData({
        ...initialFormState,
        ownerConvenio: derivadorConvenioName,
      });
      setRutError('');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError('Error al crear la solicitud. Inténtelo de nuevo.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const comunasOptions = formData.ownerRegion ? REGIONES_COMUNAS[formData.ownerRegion] || [] : [];
  const filteredBreeds = formData.species
    ? breeds.filter((b) => b.species === formData.species)
    : [];

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Registrar Nueva Solicitud
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Registra una nueva solicitud creando al tutor y al paciente en un solo paso.
      </Typography>
      <Box component="form" onSubmit={handleSubmit}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Datos del Tutor</Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 6
            }}>
            <TextField
              fullWidth
              label="Nombre *"
              name="ownerName"
              value={formData.ownerName}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <TextField
              fullWidth
              label="RUT"
              name="ownerRut"
              value={formData.ownerRut}
              onChange={handleChange}
              required
              error={!!rutError}
              helperText={rutError || 'Formato: 12345678-9'}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <TextField
              fullWidth
              label="Celular *"
              name="ownerCelular"
              value={formData.ownerCelular}
              onChange={handleChange}
              required
              type="tel"
              InputProps={{
                startAdornment: <InputAdornment position="start">+56</InputAdornment>,
              }}
              error={!!phoneError}
              helperText={phoneError || 'Ingresa un número de 9 dígitos, por ejemplo 9XXXXXXXX'}
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <TextField
              fullWidth
              label="Email de Contacto"
              name="ownerMail"
              value={formData.ownerMail}
              onChange={handleChange}
              type="email"
              required
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <TextField
              fullWidth
              select
              label="Región"
              name="ownerRegion"
              value={formData.ownerRegion}
              onChange={handleChange}
              required
            >
              <MenuItem value="">Seleccionar región...</MenuItem>
              {Object.keys(REGIONES_COMUNAS).map((region) => (
                <MenuItem key={region} value={region}>
                  {region}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <TextField
              fullWidth
              select
              label="Comuna"
              name="ownerComuna"
              value={formData.ownerComuna}
              onChange={handleChange}
              required
              disabled={!formData.ownerRegion}
            >
              <MenuItem value="">Seleccionar comuna...</MenuItem>
              {comunasOptions.map((comuna) => (
                <MenuItem key={comuna} value={comuna}>
                  {comuna}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <TextField
              fullWidth
              select
              label="Convenio"
              name="ownerConvenio"
              value={formData.ownerConvenio}
              onChange={handleChange}
              required
              helperText={
                isDerivador
                  ? 'Convenio asignado por el administrador'
                  : convenios.length === 0
                    ? 'No hay convenios configurados. Ve a Configuración → Convenios'
                    : `${convenios.length} convenios disponibles`
              }
              disabled={isDerivador}
            >
              <MenuItem value="">Seleccionar convenio...</MenuItem>
              {convenios
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((conv) => (
                  <MenuItem key={conv.id} value={conv.name}>
                    {conv.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Datos del Paciente</Typography>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              fullWidth
              label="Nombre del Paciente *"
              name="patientName"
              value={formData.patientName}
              onChange={handleChange}
              required
            />
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              fullWidth
              select
              label="Especie *"
              name="species"
              value={formData.species}
              onChange={handleChange}
              required
            >
              <MenuItem value="Canino">Canino</MenuItem>
              <MenuItem value="Felino">Felino</MenuItem>
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              fullWidth
              select
              label="Raza"
              name="breed"
              value={formData.breed}
              onChange={handleChange}
              required
              disabled={!formData.species}
              helperText={
                !formData.species
                  ? 'Selecciona primero una especie'
                  : filteredBreeds.length === 0
                    ? 'No hay razas cargadas. Ve a Configuración → Razas'
                    : `${filteredBreeds.length} razas disponibles`
              }
            >
              <MenuItem value="">Seleccionar raza...</MenuItem>
              {filteredBreeds
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((breed) => (
                  <MenuItem key={breed.id} value={breed.name}>
                    {breed.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 2
            }}>
            <TextField
              fullWidth
              select
              label="Género"
              name="genero"
              value={formData.genero}
              onChange={handleChange}
              required
            >
              <MenuItem value="">Sin especificar</MenuItem>
              <MenuItem value="Macho">Macho</MenuItem>
              <MenuItem value="Hembra">Hembra</MenuItem>
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 2
            }}>
            <TextField
              fullWidth
              label="Fecha de Nacimiento"
              name="birthDate"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.birthDate}
              onChange={handleChange}
              required
            />
          </Grid>
        </Grid>

        <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>Datos de la Solicitud</Typography>
        <Grid container spacing={2}>
          <Grid
            size={{
              xs: 12,
              sm: 4
            }}>
            <TextField
              fullWidth
              select
              label="Necesidad de Atención"
              name="attentionNeed"
              value={formData.attentionNeed}
              onChange={handleChange}
              required
              helperText={
                needs.length === 0
                  ? 'No hay necesidades cargadas. Ve a Configuración → Necesidades'
                  : `${needs.length} necesidades disponibles`
              }
            >
              <MenuItem value="">Seleccionar necesidad...</MenuItem>
              {needs
                .filter((n) => n.isActive !== false)
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                .map((need) => (
                  <MenuItem key={need.id} value={need.name}>
                    {need.name}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 3
            }}>
            <TextField
              fullWidth
              select
              label="Prioridad"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              required
            >
              <MenuItem value="Urgente">Urgente</MenuItem>
              <MenuItem value="Alta">Alta</MenuItem>
              <MenuItem value="Media">Media</MenuItem>
            </TextField>
          </Grid>
          <Grid
            size={{
              xs: 12,
              sm: 5
            }}>
            <TextField
              fullWidth
              label="Información adicional"
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              multiline
              minRows={3}
              placeholder="Detalle brevemente el motivo de la consulta, antecedentes clínicos, etc."
              required
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !!rutError}
          >
            {loading ? <CircularProgress size={24} /> : 'Enviar Solicitud'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default AddContactRequestForm;
